import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'

const SLICE_NAME = 'siteNotes'

function prepConfig(getState: any) {
	const {
		userLogin: { userInfo },
	} = getState()
	return { headers: { Authorization: `Bearer ${userInfo.token}` } }
}

interface SiteNotesState {
	entities: Record<string, any>
	/** Ids for the site currently loaded into `entities`, newest first. */
	ids: string[]
	loadedSiteId: string | null
	isLoading: boolean
	error: string | null
}

const initialState: SiteNotesState = {
	entities: {},
	ids: [],
	loadedSiteId: null,
	isLoading: false,
	error: null,
}

export const getSiteNotes = createAsyncThunk(
	`${SLICE_NAME}/get`,
	async (siteId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.get('/api/sitenotes/notes', {
				params: { siteid: siteId },
				...prepConfig(getState),
			})
			return { siteId, notes: data }
		} catch {
			return rejectWithValue('Failed to load site notes')
		}
	},
)

export const createSiteNote = createAsyncThunk(
	`${SLICE_NAME}/create`,
	async (note: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.post('/api/sitenotes', note, prepConfig(getState))
			return data
		} catch {
			return rejectWithValue('Failed to save site note')
		}
	},
)

export const updateSiteNote = createAsyncThunk(
	`${SLICE_NAME}/update`,
	async (note: any, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(
				`/api/sitenotes/${note._id}`,
				note,
				prepConfig(getState),
			)
			return data
		} catch {
			return rejectWithValue('Failed to update site note')
		}
	},
)

/**
 * Delete a site note and the images it carried.
 *
 * Unlike the other thunks here, this one keeps the server's message. The others
 * can afford a generic "Failed to save site note" because a failure there is a
 * fault; a delete refusal is a *reason* — "this note has 2 comments on it, so it
 * cannot be deleted" — and replacing it with something generic throws away the
 * only explanation the user gets.
 *
 * Returns the id it was given. `PUT /api/sitenotes/:id/delete` answers with
 * `{ message, siteid, imagesRemoved }` and no note id, so the reducer would
 * otherwise have nothing to remove.
 */
export const deleteSiteNote = createAsyncThunk(
	`${SLICE_NAME}/delete`,
	async (noteId: string, { getState, rejectWithValue }) => {
		try {
			const { data } = await axios.put(
				`/api/sitenotes/${noteId}/delete`,
				{},
				prepConfig(getState),
			)
			return { noteId, ...data }
		} catch (error: any) {
			return rejectWithValue(
				error?.response?.data?.message || 'Failed to delete this note.',
			)
		}
	},
)

const siteNoteSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder
			.addCase(getSiteNotes.pending, state => {
				state.isLoading = true
				state.error = null
			})
			.addCase(getSiteNotes.fulfilled, (state, action) => {
				state.isLoading = false
				state.entities = {}
				state.ids = []
				action.payload.notes.forEach((note: any) => {
					state.entities[note._id] = note
					state.ids.push(note._id)
				})
				state.loadedSiteId = action.payload.siteId
			})
			.addCase(getSiteNotes.rejected, (state, action) => {
				state.isLoading = false
				state.error = action.payload as string
			})
			// Deliberately no `pending` case for create. locationNoteSlice flips
			// isLoading/isInitialized on every create, which flashes a spinner
			// once per note when the wizard writes several in a row.
			.addCase(createSiteNote.fulfilled, (state, action) => {
				const note = action.payload
				state.entities[note._id] = note
				if (!state.ids.includes(note._id)) state.ids.unshift(note._id)
			})
			.addCase(createSiteNote.rejected, (state, action) => {
				state.error = action.payload as string
			})
			.addCase(updateSiteNote.fulfilled, (state, action) => {
				const note = action.payload
				state.entities[note._id] = note
				if (!state.ids.includes(note._id)) state.ids.unshift(note._id)
			})
			.addCase(deleteSiteNote.fulfilled, (state, action) => {
				const { noteId } = action.payload
				delete state.entities[noteId]
				state.ids = state.ids.filter(id => id !== noteId)
			})
		// Deliberately no `rejected` case: the refusal is shown in the delete
		// dialog by the caller, which keeps it open. Writing it to `state.error`
		// as well would put the same message in the page-level banner, where it
		// would outlive the dialog it belongs to.
	},
})

export default siteNoteSlice
